import { DashboardConfigurationDependencies } from '../../dashboard-config/dashboard-config.model';
import { PeriodKind } from '../../dashboard-config/metrics/metric-profile';
import { YearWindow } from '../../dashboard-config/product-rules';
import {
  DashboardDataTransformer,
  DefinedPeriodIdentifiersToPeriodLabelDashboardDataTransformer,
  Q2ToYearPeriodIdDashboardDataTransformer,
} from '../../data_transformers/dashboard_data_transformer';
import { DashboardFilter } from '../../filters/dashboard_filter';
import {
  DashboardAutomaticPeriodSingleSelectFilter,
  DashboardAutomaticPeriodsRangeFilter,
  DashboardAutomaticYearSingleSelectFilter,
  DashboardAutomaticYearsRangeFilter,
  DashboardPeriodTypeSingleSelectFilter,
  DashboardYearSingleSelectFilter,
  DashboardYearsRangeFilter,
} from '../../filters/dashboard_filter_implementations';
import type { DashboardAxes } from './dashboard-axes';

const ALL_PERIOD_KINDS: readonly PeriodKind[] = ['year', 'quarter', 'month'];

/** One point in time (ranking, index, structure) or a range (time series, comparison, forecast). */
export type TimeCardinality = 'single' | 'range';

/**
 * A dashboard's time filter, resolved once: year or period from the metric (`temporal`) unless the
 * analysis' datasets are yearly, single or range from the view, fixed window or automatic from
 * `ProductRules` (`axes.yearWindow`).
 */
export type TimeFilterConfig =
  | {
      kind: 'year';
      cardinality: TimeCardinality;
      /** Fixed year window (district level); automatic years when absent. */
      window?: YearWindow;
      /** Years initially selected by an automatic range; absent: every year. */
      defaultYearRange?: number;
    }
  | {
      kind: 'period';
      cardinality: TimeCardinality;
      periodKinds: readonly PeriodKind[];
      maxYearOffset?: number;
    };

export function resolveTimeFilter(
  axes: DashboardAxes,
  cardinality: TimeCardinality,
  options: {
    /** Years initially selected by an automatic range. Default 3; `all` selects every year (dossiers). */
    defaultYearRange?: number | 'all';
    /** The analysis' datasets are yearly for every metric (index, forecast). */
    yearly?: boolean;
  } = {},
): TimeFilterConfig {
  const temporal = axes.metric.temporal;
  if (temporal.kind === 'period' && !options.yearly) {
    return { kind: 'period', cardinality, periodKinds: temporal.periodKinds, maxYearOffset: temporal.maxYearOffset };
  }
  const defaultYearRange = options.defaultYearRange ?? 3;
  return {
    kind: 'year',
    cardinality,
    window: axes.yearWindow,
    ...(defaultYearRange === 'all' ? {} : { defaultYearRange }),
  };
}

/** Period kinds the metric's dataset lacks; hidden in the period type filter. */
function missingPeriodKinds(time: { periodKinds: readonly PeriodKind[] }): PeriodKind[] {
  return ALL_PERIOD_KINDS.filter((kind) => !time.periodKinds.includes(kind));
}

export function createTimeFilters(deps: DashboardConfigurationDependencies, time: TimeFilterConfig): DashboardFilter<any>[] {
  if (time.kind === 'period') {
    const excludedOptions = missingPeriodKinds(time);
    const periodType = new DashboardPeriodTypeSingleSelectFilter({
      name: 'period',
      renderAtChildFilterComponent: true,
      showLabel: false,
      showFullOptionLabel: true,
      ...(excludedOptions.length ? { excludedOptions } : {}),
    });
    return [
      periodType,
      time.cardinality === 'range'
        ? new DashboardAutomaticPeriodsRangeFilter(deps.store, deps.datasetName, {
            name: 'periods',
            parentFilterName: 'period',
            ...(time.maxYearOffset !== undefined ? { maxYearOffset: time.maxYearOffset } : {}),
          })
        : new DashboardAutomaticPeriodSingleSelectFilter(deps.store, deps.datasetName, { parentFilterName: 'period' }),
    ];
  }

  const window = time.window;
  if (time.cardinality === 'single') {
    return [
      window
        ? new DashboardYearSingleSelectFilter({ minYear: window.minYear, maxYear: window.maxYear })
        : new DashboardAutomaticYearSingleSelectFilter(deps.store, deps.datasetName, {}),
    ];
  }
  return [
    window
      ? new DashboardYearsRangeFilter({
          minYear: window.minYear,
          maxYear: window.maxYear,
          defaultMinYear: window.defaultMinYear ?? window.minYear,
        })
      : new DashboardAutomaticYearsRangeFilter(deps.store, deps.datasetName, { defaultYearRange: time.defaultYearRange }),
  ];
}

/**
 * Period rows get readable labels; limited to the metric's period kinds when it lacks some.
 * Follows the metric's datasets, not the time filter: index ranking filters by year, yet its
 * Employed rows are periods.
 */
export function createPeriodLabelTransformers(
  deps: DashboardConfigurationDependencies,
  axes: DashboardAxes,
): DashboardDataTransformer[] {
  const temporal = axes.metric.temporal;
  if (temporal.kind !== 'period') return [];
  const enabledPeriods = missingPeriodKinds(temporal).length ? [...temporal.periodKinds] : undefined;
  return [
    enabledPeriods
      ? new DefinedPeriodIdentifiersToPeriodLabelDashboardDataTransformer(deps.store, deps.datasetName, { enabledPeriods })
      : new DefinedPeriodIdentifiersToPeriodLabelDashboardDataTransformer(deps.store, deps.datasetName),
  ];
}

/** Rows of the yearly period type take their year as `period_id` (`MetricTemporal.yearAsPeriodId`). */
export function createPeriodYearTransformers(axes: DashboardAxes): DashboardDataTransformer[] {
  const temporal = axes.metric.temporal;
  return temporal.kind === 'period' && temporal.yearAsPeriodId ? [new Q2ToYearPeriodIdDashboardDataTransformer()] : [];
}

/** The time filter a chart series or timeline reads, and the row column its values match. */
export function timeFilterDetail(time: TimeFilterConfig): {
  series: { filterName: string; filterValueKey: string };
  timeline: { filterName: string; optionValueKey: string };
} {
  return time.kind === 'period'
    ? { series: { filterName: 'periods', filterValueKey: 'period_id' }, timeline: { filterName: 'period_value', optionValueKey: 'period_id' } }
    : { series: { filterName: 'years', filterValueKey: 'year' }, timeline: { filterName: 'year', optionValueKey: 'year' } };
}
